import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageSquareText, Send, Eye, CalendarClock, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";

const QUALITY_TONE = { GREEN: "bg-emerald-100 text-emerald-700", YELLOW: "bg-amber-100 text-amber-700", RED: "bg-rose-100 text-rose-700" };

export default function MonthlyRecapManager() {
  const qc = useQueryClient();
  const [preview, setPreview] = useState(null);
  const [sendDay, setSendDay] = useState(10);
  const [enabled, setEnabled] = useState(true);
  const [bulan, setBulan] = useState("");

  const { data: cfg } = useQuery({ queryKey: ["wa-recap-config"], queryFn: async () => (await api.get("/wa/recap/config")).data });
  const { data: log = [] } = useQuery({ queryKey: ["wa-recap-log"], queryFn: async () => (await api.get("/wa/recap/log")).data });
  useEffect(() => { if (cfg) { setSendDay(cfg.send_day); setEnabled(cfg.enabled); } }, [cfg]);

  const saveMut = useMutation({
    mutationFn: async () => (await api.put("/wa/recap/config", { send_day: Number(sendDay), enabled })).data,
    onSuccess: () => { toast.success("Pengaturan rekap disimpan"); qc.invalidateQueries({ queryKey: ["wa-recap-config"] }); },
    onError: (e) => toast.error(errMsg(e, "Gagal menyimpan")),
  });
  const previewMut = useMutation({
    mutationFn: async () => (await api.get("/wa/recap/preview")).data,
    onSuccess: (d) => setPreview(d),
    onError: (e) => toast.error(errMsg(e, "Gagal memuat pratinjau")),
  });
  const sendMut = useMutation({
    mutationFn: async () => (await api.post("/wa/recap/send-now", { bulan: bulan.trim() })).data,
    onSuccess: (d) => {
      toast.success(`Rekap diproses: ${d.sent} terkirim${d.simulasi ? " (SIMULASI)" : ""}, ${d.failed} gagal, ${d.skipped} dilewati, target ${d.target}`);
      qc.invalidateQueries({ queryKey: ["wa-recap-log"] });
    },
    onError: (e) => toast.error(errMsg(e, "Gagal mengirim")),
  });

  const health = cfg?.health || {};
  const quality = health.quality_rating || "UNKNOWN";

  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="monthly-recap-manager">
      <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300"><MessageSquareText className="h-5 w-5" /><span className="font-bold text-lg">Rekap Keaktifan Bulanan (WhatsApp)</span></div>
      <div className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-300">
        <CalendarClock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <p className="min-w-0">Terjadwal otomatis <strong>tanggal {cfg?.send_day ?? 10} tiap bulan, 10:00 WIB</strong> untuk rekap bulan sebelumnya ({cfg?.prev_month || "-"}). Dikirim sebagai <strong>PDF + gambar (JPG)</strong> lewat WhatsApp Cloud API resmi, <strong>hanya ke wali yang menyetujui</strong> (opt-in). Wali dapat berhenti dengan membalas <strong>STOP</strong>.</p>
      </div>

      {/* Status koneksi & Quality Rating */}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="wa-health">
        <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300"><ShieldCheck className="h-3.5 w-3.5" /> Status:</span>
        {health.configured ? <Badge className="border-0 bg-emerald-100 text-emerald-700">Terhubung</Badge>
          : <Badge className="border-0 bg-amber-100 text-amber-700">Mode Simulasi (kredensial belum diisi)</Badge>}
        <span className="text-slate-500 dark:text-slate-400">Quality Rating:</span>
        <Badge className={`border-0 ${QUALITY_TONE[quality] || "bg-slate-100 text-slate-600"}`} data-testid="wa-quality-badge">{quality}</Badge>
        {health.messaging_limit_tier && <span className="text-slate-400">Limit: {health.messaging_limit_tier}</span>}
      </div>

      {/* Konfigurasi jadwal */}
      <div className="grid sm:grid-cols-3 gap-3 items-end border-t border-emerald-100 dark:border-white/10 pt-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Tanggal kirim tiap bulan (1–28)</Label>
          <Input type="number" min={1} max={28} value={sendDay} data-testid="recap-send-day" onChange={(e) => setSendDay(e.target.value)} className="h-10" />
        </div>
        <div className="flex items-center gap-2 h-10">
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="recap-enabled-switch" />
          <span className="text-sm text-slate-600 dark:text-slate-300">{enabled ? "Pengiriman otomatis aktif" : "Nonaktif"}</span>
        </div>
        <Button className="h-10 bg-emerald-700 hover:bg-emerald-800" data-testid="recap-save-config" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>Simpan Pengaturan</Button>
      </div>

      {/* Aksi manual */}
      <div className="flex gap-2 flex-wrap items-end border-t border-emerald-100 dark:border-white/10 pt-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Kirim manual — Bulan (YYYY-MM, kosong = bulan lalu)</Label>
          <Input value={bulan} onChange={(e) => setBulan(e.target.value)} placeholder={cfg?.prev_month || "2026-09"} className="h-10 w-44" data-testid="recap-manual-bulan" />
        </div>
        <Button variant="outline" className="h-10 gap-2 rounded-xl bg-white/60 dark:bg-white/5" data-testid="recap-preview-button" disabled={previewMut.isPending} onClick={() => previewMut.mutate()}><Eye className="h-4 w-4" /> Pratinjau Penerima</Button>
        <Button className="h-10 gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800" data-testid="recap-send-now-button" disabled={sendMut.isPending}
          onClick={() => { if (window.confirm("Kirim Rekap Bulanan sekarang ke seluruh wali yang opt-in?")) sendMut.mutate(); }}><Send className="h-4 w-4" /> Kirim Sekarang</Button>
      </div>

      {log.length > 0 && (
        <div className="space-y-1.5 border-t border-emerald-100 dark:border-white/10 pt-4">
          <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300">Riwayat Pengiriman Terakhir</p>
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {log.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs bg-white/60 dark:bg-white/5 rounded-lg px-3 py-1.5" data-testid={`recap-log-${r.id}`}>
                <span className="text-slate-600 dark:text-slate-300 truncate">{r.nama_santri} · {r.no_induk} · {r.phone} · {r.bulan}{r.simulation ? " · SIMULASI" : ""}</span>
                <span className="flex items-center gap-1 shrink-0">
                  {r.ok ? <Badge className="border-0 bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" /> {r.simulation ? "sim" : "terkirim"}</Badge>
                    : <Badge className="border-0 bg-rose-100 text-rose-700"><AlertTriangle className="h-3 w-3 mr-1" /> gagal</Badge>}
                  <span className="text-slate-400 hidden sm:inline">{new Date(r.created_at).toLocaleString("id-ID")}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="recap-preview-dialog">
          <DialogHeader><DialogTitle>Pratinjau Penerima Rekap</DialogTitle></DialogHeader>
          <div className={`text-xs rounded-xl px-3 py-2 ${preview?.simulasi ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}>
            {preview?.simulasi ? "MODE SIMULASI — kredensial WhatsApp Cloud API belum diisi. Pesan tidak benar-benar terkirim, hanya dicatat." : "Terhubung ke WhatsApp Cloud API — pesan akan dikirim."} · {preview?.total ?? 0} wali (opt-in) akan menerima rekap {preview?.prev_month}.
          </div>
          <div className="space-y-2">
            {(preview?.items || []).map((m) => (
              <div key={m.no_induk} className="bg-white/60 dark:bg-white/5 rounded-xl p-3 text-xs" data-testid={`recap-preview-item-${m.no_induk}`}>
                <p className="font-semibold text-emerald-900 dark:text-emerald-100">{m.nama_santri} · {m.no_induk}</p>
                <p className="text-slate-500 dark:text-slate-300">Wali: {m.nama_wali || "-"} · {m.phone}</p>
              </div>
            ))}
            {(preview?.items || []).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Belum ada wali yang menyetujui (opt-in) notifikasi WhatsApp.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
